import { useEffect, useState } from "react";
import { DownloadSimple, Trash, CheckCircle, CircleNotch, WarningCircle, FilmStrip } from "@phosphor-icons/react";
import { api, act, useFetch } from "@/lib/api";
import { Badge } from "@/components/common";
import { Button } from "@/components/ui/button";

export const STYLES = [
  { value: "photo", label: "Photo-realistic", hint: "Editorial photography, real settings" },
  { value: "illustrated", label: "Bold illustrated", hint: "Flat vector, punchy poster graphics" },
];

export const StyleToggle = ({ value, onChange, testid = "style" }) => (
  <div className="grid grid-cols-2 gap-2">
    {STYLES.map((s) => (
      <button key={s.value} type="button" data-testid={`${testid}-${s.value}`} onClick={() => onChange(s.value)}
        className={`text-left rounded-md border p-3 transition-colors ${value === s.value ? "border-[#007AFF] bg-[#007AFF]/10" : "border-border hover:border-zinc-500"}`}>
        <div className="text-sm font-semibold">{s.label}</div>
        <div className="text-[11px] text-zinc-500">{s.hint}</div>
      </button>
    ))}
  </div>
);

export const Chips = ({ value, onChange, options, testid }) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map((o) => (
      <button key={o} type="button" data-testid={`${testid}-${o.replace(/[^a-z0-9]/gi, "-")}`} onClick={() => onChange(o)}
        className={`font-mono text-[11px] px-2.5 py-1 rounded-full border transition-colors ${value === o ? "bg-white text-black border-white" : "border-border text-zinc-400 hover:text-white"}`}>{o}</button>
    ))}
  </div>
);

export function useAssets(kind) {
  const f = useFetch(`/studio/assets${kind ? `?kind=${kind}` : ""}`);
  const busy = (f.data || []).some((a) => a.status === "generating");
  useEffect(() => {
    if (!busy) return undefined;
    const t = setInterval(f.reload, 5000);
    return () => clearInterval(t);
  }, [busy, f.reload]);
  return f;
}

export const Media = ({ a, className = "", style }) => {
  if (!a) return null;
  if (a.status === "generating") return (
    <div style={style} className={`flex flex-col items-center justify-center gap-2 bg-black text-zinc-500 text-xs font-mono shimmer ${className}`} data-testid={`asset-generating-${a.id}`}>
      <CircleNotch className="animate-spin" size={22} /> {a.kind === "video" ? "Rendering video (1–4 min)" : "Generating image (~30s)"}
    </div>
  );
  if (a.status === "failed") return <div style={style} className={`flex flex-col items-center justify-center gap-1 bg-black text-[#FF6961] text-xs p-3 text-center ${className}`}><WarningCircle size={20} />{a.error}</div>;
  return a.kind === "video"
    ? <video style={style} src={`${a.url}#t=0.5`} preload="metadata" controls playsInline className={`bg-black object-contain ${className}`} data-testid={`asset-video-${a.id}`} />
    : <img style={style} src={a.url} alt={a.brief} className={`bg-black object-contain ${className}`} data-testid={`asset-image-${a.id}`} />;
};

export const AssetCard = ({ a, onChanged }) => {
  const approve = async () => { await act(() => api.post(`/studio/assets/${a.id}/approve`), "Approved for use"); onChanged(); };
  const [anim, setAnim] = useState(false);
  const animate = async () => { setAnim(true); const r = await act(() => api.post(`/studio/assets/${a.id}/animate`, { duration: 5 }), "Animating — find the clip in the Videos tab (2–4 min)"); setAnim(false); if (r) onChanged(); };
  const del = async () => { await act(() => api.delete(`/studio/assets/${a.id}`), "Removed"); onChanged(); };
  return (
    <div className="group border border-border rounded-md overflow-hidden bg-[#121212] rise" data-testid={`asset-card-${a.id}`}>
      <Media a={a} className="w-full max-h-[520px]" style={{ aspectRatio: a.aspect.replace(":", " / ") }} />
      <div className="p-3 space-y-2">
        <div className="text-xs text-zinc-300 line-clamp-2">{a.brief}</div>
        {a.motion && <div className="text-[11px] text-zinc-500 line-clamp-2" data-testid={`asset-motion-${a.id}`}>Motion: {a.motion}</div>}
        <div className="flex items-center gap-1.5 flex-wrap">
          <Badge v={a.style} tone="blue" /><Badge v={a.aspect} />{a.approved ? <Badge v="approved" /> : <Badge v={a.status} />}
        </div>
        {a.status === "ready" && <div className="flex gap-1 pt-1">
          <Button asChild size="sm" variant="secondary" className="gap-1 h-7 text-xs"><a href={`${a.url}&download=1`} data-testid={`asset-download-${a.id}`}><DownloadSimple /> Download</a></Button>
          {!a.approved && <Button size="sm" variant="secondary" className="gap-1 h-7 text-xs" data-testid={`asset-approve-${a.id}`} onClick={approve}><CheckCircle /> Approve</Button>}
          {a.approved && a.kind === "image" && <Button size="sm" variant="secondary" className="gap-1 h-7 text-xs" disabled={anim} data-testid={`asset-animate-${a.id}`} onClick={animate}>{anim ? <CircleNotch className="animate-spin" /> : <FilmStrip />} {anim ? "Starting…" : "Animate"}</Button>}
          <Button size="icon" variant="ghost" className="h-7 w-7 ml-auto" data-testid={`asset-delete-${a.id}`} onClick={del}><Trash /></Button>
        </div>}
        {a.status === "failed" && <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`asset-delete-${a.id}`} onClick={del}><Trash /> Remove</Button>}
      </div>
    </div>
  );
};
